<template>
  <div
    data-testid="qatc"
    :class="{
      QATC: true,
      [NativeAdClickOrigin.QuickAddToCart]: true,
      'QATC--with-feedback': willShowFeedback || isShowingFeedback,
    }"
  >
    <AkButton
      :class="{ QATC__button: true, 'ds-absolute ds-w-full': !mobile }"
      data-testid="qatc-button"
      :disabled="isShowingFeedback"
      outlined
      color="white"
      multiline
      @click="clickHandler"
    >
      <AkIcon
        :class="{ 'ds-mr-0.5 ds-pb-0.5': !mobile }"
        :symbol="isShowingFeedback ? 'check' : 'plus'"
        size="sm"
      />
      <span v-if="!mobile">{{ buttonLabel }}</span>
    </AkButton>
    <Teleport
      v-if="product.variants?.length > 1 && priceBreakdown"
      to="body"
    >
      <AkModal
        ref="quickAddToCartModal"
        size="xl"
        :with-padding="false"
        data-testid="addToCartModal"
      >
        <AddToCartPopin
          :product-id="product.id"
          :price-breakdown="priceBreakdown"
          @add-to-cart-variant="$emit('variantAddedToCart', $event)"
        />
      </AkModal>
    </Teleport>
  </div>
</template>

<script lang="ts">
// @ts-nocheck // This component is planned to be replaced with a new QATC UX that requires a complete rewrite
import { debounce, type DebouncedFunc } from '@core/utilities/lodash-utils';
import { defineComponent, PropType, ref, watchEffect } from 'vue';
import { BrandProperty } from '@monolith/legacy/services/analytics/properties/brand-property';
import Product, { Option } from '@monolith/legacy/types/product';
import type { ESProductHit } from '@bc/discovery/types';
import { AkIcon } from '@ankorstore/design-system';
import { CanAddFirstVariantToCartUseCase } from '@monolith/legacy/modules/cart-2/service/can-add-first-variant-to-cart.use-case';
import { useEventBus } from '@bc/shared';
import { NativeAdClickOrigin } from '@bc/advertisement';
import { getVariantPriceFields, isProductMultiple } from '@monolith/legacy/services/product-variants';
import { getDiscount, getPriceBreakdown } from '@monolith/legacy/services/utils/tooling';
import StoreRootHelpers from '@monolith/legacy/store/helpers';
import CartHelpers from '@monolith/legacy/store/cart/helpers';
import usePrice from '@client/monolith/core/composables/use-price';
import AddToCartPopin from '@monolith/legacy/components/popins/add-to-cart-popin.vue';

interface QuickAddToCartData {
  item: {
    option: Partial<Option> & { unit_multiplier: number; name: string };
    brand: BrandProperty;
    quantity: number;
  };
  hasVariants: boolean;
  itemsToAddNext: number;
  willShowFeedback: boolean;
  feedbackTimeoutID: number;
  debouncedExecuteUpdateActions: DebouncedFunc<() => Promise<void>>;
}

const CLICK_DEBOUNCE_TIME = 550;
const FEEDBACK_TIME = 2000;

export default defineComponent({
  name: 'QuickAddToCart',
  components: {
    AkIcon,
    AddToCartPopin,
  },
  props: {
    mobile: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      required: false,
      default: undefined,
    },
    pageNumber: {
      type: Number,
      required: false,
      default: undefined,
    },
    product: {
      type: Object as PropType<Product | ESProductHit>,
      required: true,
    },
    parentSectionId: {
      type: String,
      default: undefined,
    },
    parentComponent: {
      type: String as PropType<string>,
      default: null,
    },
    isSponsored: {
      type: Boolean,
      required: false,
    },
    // Temporary prop to disable tracking while we work on RET-3307
    disableTracking: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clicked', 'clickVariantsModal', 'productAddedToCart', 'variantAddedToCart'],
  expose: ['execute'],
  setup(props) {
    const quickAddToCartModal = ref(null);
    const { getConvertedPrice } = usePrice();
    const { eventBus } = useEventBus();
    const { productByOptionId } = StoreRootHelpers.useGetters(['productByOptionId']);
    const { itemByOptionId } = CartHelpers.useGetters(['itemByOptionId']);
    const { addToCart: addToCartAction, updateItemQuantity: updateItemQuantityAction } = CartHelpers.useActions([
      'addToCart',
      'updateItemQuantity',
    ]);

    const priceBreakdown = ref<ReturnType<typeof getPriceBreakdown>>();
    watchEffect(async () => {
      let { original_wholesale_price, wholesale_price, retail_price } = props.product;
      if (isProductMultiple(props.product)) {
        const variant = props.product.variants[0];
        if (!variant) {return;}
        ({ original_wholesale_price, wholesale_price, retail_price } = getVariantPriceFields(variant));
      }

      if (!original_wholesale_price || !wholesale_price || !retail_price) {return;}

      priceBreakdown.value = getPriceBreakdown(
        await getConvertedPrice(original_wholesale_price),
        await getConvertedPrice(retail_price),
        await getConvertedPrice(wholesale_price),
        getDiscount(props.product.discount_rate, 'brand')
      );
    });

    return {
      eventBus,
      NativeAdClickOrigin,
      productByOptionId,
      itemByOptionId,
      addToCartAction,
      updateItemQuantityAction,
      priceBreakdown,
      quickAddToCartModal,
    };
  },
  data(): QuickAddToCartData {
    return {
      item: {
        option: { unit_multiplier: 1, name: '' } as QuickAddToCartData['item']['option'],
        brand: undefined as BrandProperty,
        quantity: 0,
      },
      hasVariants: undefined,
      itemsToAddNext: 0,
      willShowFeedback: false,
      feedbackTimeoutID: undefined as number,
      debouncedExecuteUpdateActions: undefined as QuickAddToCartData['debouncedExecuteUpdateActions'],
    };
  },
  computed: {
    buttonLabel(): string {
      return this.isShowingFeedback
        ? this.$tc(
            '{count} item added to cart! | {count} item added to cart! | {count} items added to cart!',
            this.itemsToAddNext * this.product.unit_multiplier
          )
        : this.$tc(
            'Quick add of {count} product | Quick add of {count} product | Quick add of {count} products',
            this.product.unit_multiplier
          );
    },
    isShowingFeedback(): boolean {
      return Number.isInteger(this.feedbackTimeoutID);
    },
  },
  watch: {
    product: {
      handler: function () {
        this.item.brand = this.product.brand;
        const variant = this.product.variants[0];
        this.item.option = {
          ...this.product.options.find(({ sku }) => sku === variant?.sku),
          unit_multiplier: this.product.unit_multiplier,
          name: this.product.name as string,
        };
        this.item.quantity =
          this.itemByOptionId(this.item.option?.id) !== null ? this.itemByOptionId(this.item.option.id).quantity : 0;
        this.hasVariants = Boolean(this.product.variants.length > 1);
      },
      immediate: true,
    },
  },
  created() {
    this.debouncedExecuteUpdateActions = debounce(this.executeUpdateActions, CLICK_DEBOUNCE_TIME);
  },
  unmounted() {
    this.feedbackTimeoutID && window.clearTimeout(this.feedbackTimeoutID);
  },
  methods: {
    /**
     * Exposed method to be called from parent component to execute the action of the button
     */
    execute() {
      this.clickHandler();
    },
    clickHandler() {
      this.$emit('clicked');
      this.willShowFeedback = true;
      this.item.quantity += 1;
      this.itemsToAddNext += 1;
      this.debouncedExecuteUpdateActions();
    },
    async executeUpdateActions() {
      if (this.hasVariants) {
        this.clearFeedbackState();
        (this.quickAddToCartModal as DS['AkModal']).openModal();
        return;
      }

      const canAddToCart = await new CanAddFirstVariantToCartUseCase(this.$store).execute(this.product, this.item.quantity);

      if (!canAddToCart) {
        return;
      }

      this.displayFeedback();
      if (this.itemByOptionId(this.item.option?.id)) {
        return this.executeUpdateQuantityAction();
      }
      await this.executeAddToCartAction();

      const { brand, option, quantity } = this.item;
      const payload = {
        brand,
        option: { id: option.id, name: option.name as string },
        variant: null,
        quantity,
      };
      this.$emit('productAddedToCart', payload);
    },
    displayFeedback() {
      this.feedbackTimeoutID = window.setTimeout(this.clearFeedbackState, FEEDBACK_TIME);
    },
    clearFeedbackState() {
      this.willShowFeedback = false;
      this.itemsToAddNext = 0;
      this.feedbackTimeoutID = undefined;
    },
    executeUpdateQuantityAction() {
      const { brand, quantity } = this.item;

      this.updateItemQuantityAction({
        brand,
        item: this.itemByOptionId(this.item.option?.id),
        quantity,
      });
    },
    async executeAddToCartAction() {
      const { brand, option, quantity } = this.item;
      const { variants = [] } = this.product;
      const [variant] = variants;

      await this.addToCartAction({ brand, option, variant, quantity, noTrack: true });
    },
  },
});
</script>

<style scoped lang="scss">
@import '@css/vue-import';
.QATC {
  &__button {
    z-index: $z-index-catalog-card-items;
    transition: transform 0.025s;
    @apply ds-min-w-6.5;
    &:disabled {
      opacity: 100 !important;
    }
    &:active:not(:disabled) {
      @apply ds-bg-warm-white;
    }
  }
}
</style>
